import axios from "axios";
import {
  getToken,
  refreshAccessToken,
  logoutByUserType,
} from "../common/routines/user-operations";
import store from "../store";
import { getFullAPIBase } from "../config/env";
import { DOWNLOAD_HOST } from "../constants/axios";

const getUserTypeFromPath = () => {
  const path = window.location.pathname;
  if (path.startsWith("/admin")) return "superAdmin";
  if (path.startsWith("/provider")) return "provider";
  if (path.startsWith("/preneed")) return "preNeed";
  return "user";
};

const getUserTypeFromRequest = (config: any) => {
  const url = (config?.url || "").toLowerCase();

  // auth endpoints
  if (url.includes("/auth/provider/")) return "provider";
  if (url.includes("/auth/planner/")) return "preNeed";
  if (url.includes("/auth/admin/") || url.includes("/admin/"))
    return "superAdmin";

  // route prefixes (adjust if needed)
  if (url.includes("/provider-dashboard/") || url.includes("/provider/"))
    return "provider";
  if (url.includes("/preneed/") || url.includes("/planner/")) return "preNeed";
  if (url.includes("/admin/")) return "superAdmin";

  return getUserTypeFromPath();
};

/**
 *  Return a userType ONLY when store is confident.
 * If nothing is logged-in / not rehydrated yet -> return null so URL inference can be used.
 */
const getActiveUserTypeFromStore = (): string | null => {
  const s: any = store.getState()?.session;
  const pathUserType = getUserTypeFromPath();

  if (
    pathUserType === "superAdmin" &&
    (s?.superAdminDetails || localStorage.getItem("superAdminDetails"))
  ) {
    return "superAdmin";
  }

  if (
    pathUserType === "provider" &&
    (s?.providerDetails || localStorage.getItem("providerDetails"))
  ) {
    return "provider";
  }

  if (
    pathUserType === "preNeed" &&
    (s?.preNeedDetails || localStorage.getItem("preNeedDetails"))
  ) {
    return "preNeed";
  }

  if (
    pathUserType === "user" &&
    (s?.userDetails || localStorage.getItem("userDetails"))
  ) {
    return "user";
  }

  return null;
};

const setupAxios = () => {
  const instance = axios.create({
    baseURL: getFullAPIBase(),
    withCredentials: true,
  });

  instance.interceptors.request.use(async (config) => {
    const customConfig = config;
    customConfig.baseURL = getFullAPIBase();

    const isAuthRequest =
      config.url?.includes("/auth/") ||
      config.url === "/login" ||
      config.url === "/refresh-token";
    const isPublicAuthEndpoint =
      isAuthRequest &&
      (config.url?.includes("login") ||
        config.url?.includes("verify-otp") ||
        config.url?.includes("request-otp") ||
        config.url?.includes("forgot-password") ||
        config.url?.includes("reset-password"));

    //  prefer store role first; if unknown, fall back to request/url inference
    const storeUserType = getActiveUserTypeFromStore();
    const inferredUserType = getUserTypeFromRequest(config);
    const resolvedUserType = storeUserType ?? inferredUserType;

    customConfig.headers = customConfig.headers ?? {};
    (customConfig.headers as any)["x-user-type"] = resolvedUserType;

    // backend requirement
    (customConfig.headers as any).source = "AFH";

    if (
      !isPublicAuthEndpoint &&
      config.url !== "/login" &&
      config.url !== "/refresh-token" &&
      !config?.url?.includes(DOWNLOAD_HOST) &&
      !customConfig.headers.Authorization
    ) {
      //  token must match the resolved userType (store first)
      (customConfig.headers as any).Authorization = `Bearer ${await getToken(
        resolvedUserType
      )}`;
    }

    return customConfig;
  });

  let isRefreshing = false;
  let refreshSubscribers: ((token: string) => void)[] = [];

  const subscribeTokenRefresh = (callback: (token: string) => void) => {
    refreshSubscribers.push(callback);
  };

  const onRefreshed = (token: string) => {
    refreshSubscribers.forEach((callback) => callback(token));
    refreshSubscribers = [];
  };

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      const isLoginRequest = originalRequest.url?.includes("/login");
      const isRefreshRequest = originalRequest.url?.includes("/refresh-token");
      const isVerifyOtpRequest = originalRequest.url
        ?.toLowerCase()
        .includes("verify-otp");
      const isAdminLoginRequest = originalRequest.url?.includes("/admin/login");
      const isCheckExistingUserRequest = originalRequest.url?.includes("/check-existing-user");
      const isRequestOtpRequest = originalRequest.url?.includes("/request-otp");

      //  prefer stamped role; if missing, store; then request/url inference
      const userType =
        (originalRequest?.headers?.["x-user-type"] as string) ||
        (originalRequest?.headers?.["X-User-Type"] as string) ||
        getActiveUserTypeFromStore() ||
        getUserTypeFromRequest(originalRequest);

      if (
        error?.response?.status === 401 &&
        !originalRequest._retry &&
        !isLoginRequest &&
        !isRefreshRequest &&
        !isVerifyOtpRequest &&
        !isAdminLoginRequest &&
        !isCheckExistingUserRequest &&
        !isRequestOtpRequest
      ) {
        if (
          error?.response?.data?.message !==
          "Your role was changed recently. Please log in again"
        ) {
          if (isRefreshing) {
            return new Promise((resolve) => {
              subscribeTokenRefresh((token) => {
                originalRequest.headers = originalRequest.headers ?? {};
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(instance(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const newToken = await refreshAccessToken(userType);

            const keyName = `${userType}Details`;
            const details = JSON.parse(localStorage.getItem(keyName) || "{}");
            details.token = newToken;
            details.accessToken = newToken;
            localStorage.setItem(keyName, JSON.stringify(details));

            instance.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;

            onRefreshed(newToken);
            return instance(originalRequest);
          } catch (refreshError) {
            logoutByUserType(userType, `${userType}:SESSION_EXPIRED`);
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
      }

      if (
        (error?.response?.status === 403) ||
        (error?.response?.data?.message ===
          "Your role was changed recently. Please log in again") &&
        !isLoginRequest &&
        !isRefreshRequest &&
        !isVerifyOtpRequest &&
        !isAdminLoginRequest &&
        !isCheckExistingUserRequest &&
        !isRequestOtpRequest
      ) {
        let logoutReason = `${userType}:SESSION_EXPIRED`;
        if (
          error?.response?.data?.errorCode === "USER_ACCOUNT_INACTIVE" ||
          error?.response?.data?.data?.message === "ACCOUNT_INACTIVE" ||
          error?.response?.data?.message === "ACCOUNT_INACTIVE"
        ) {
          logoutReason = `${userType}:ACCOUNT_INACTIVE`;
        }
        logoutByUserType(userType, logoutReason);
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export default setupAxios;
